import React, { useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Banner, Checkbox, LoadingIndicator, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { BulkDocumentActionMode, bulkApproveResidentialUnitDocument, DocumentActionItem } from '@/services/documents';
import { Button } from '@/components/Buttons';
import { BaseDocument, ProjectDocument } from '@/types';
import { uniq } from 'lodash';
import theme from './DocumentApproveMultipleDialog.module.scss';
import { Trans } from '@lingui/react/macro';

interface DocumentApproveMultipleDialogProps {
    documents: (BaseDocument | ProjectDocument)[];
    residentialUnitIds: number[];
    onClose(): void;
    onApprove(residentialUnitIds: number[], params: {}): void;
    show: boolean;
    hiddenFromBuyer: boolean;
}

const DocumentApproveMultipleDialog: React.FC<DocumentApproveMultipleDialogProps> = ({
    documents,
    residentialUnitIds,
    onClose,
    onApprove,
    show,
}) => {
    const [saving, setSaving] = useState(false);
    const [approvalMode, setApprovalMode] = useState<BulkDocumentActionMode>(BulkDocumentActionMode.LatestUnpublished);
    const [publishAllVersionsEvenIfLatestPublished, setPublishAllVersionsEvenIfLatestPublished] = useState(false);

    const filterDocumentsForApproval = (
        docs: typeof documents,
        mode: BulkDocumentActionMode,
        includeLatestPublished: boolean
    ) => {
        const documentsWithUnpublishedVersions = docs.filter((doc) => doc.unpublishedVersionsCount >= 1);
        if (mode === BulkDocumentActionMode.AllUnpublished && !includeLatestPublished)
            return documentsWithUnpublishedVersions.filter((doc) => doc.latestVersionPublished !== true);
        return documentsWithUnpublishedVersions;
    };

    const allDocumentsWithUnpublishedVersions = useMemo(
        () => documents.filter((doc) => doc.unpublishedVersionsCount >= 1),
        [documents]
    );

    const documentsToProcess = useMemo(
        () => filterDocumentsForApproval(documents, approvalMode, publishAllVersionsEvenIfLatestPublished),
        [documents, approvalMode, publishAllVersionsEvenIfLatestPublished]
    );

    const countOfDocsWithLatestPublished = useMemo(
        () => documents.filter((doc) => doc.unpublishedVersionsCount > 0 && doc.latestVersionPublished === true).length,
        [documents]
    );

    const hasDocsWithLatestPublished = countOfDocsWithLatestPublished > 0;
    const allSelectedDocumentsHaveLatestPublished = useMemo(
        () =>
            documents.length > 0 &&
            documents.every((doc) => doc.unpublishedVersionsCount > 0 && doc.latestVersionPublished === true),
        [documents]
    );

    const isPublishingLatestOnly = approvalMode === BulkDocumentActionMode.LatestUnpublished;
    const isPublishingAllVersions = approvalMode === BulkDocumentActionMode.AllUnpublished;
    const hasMultipleVersions = documents.some((doc) => doc.unpublishedVersionsCount > 1);

    const calculateTotalVersionsToPublish = (
        mode: BulkDocumentActionMode,
        filteredDocs: typeof documentsToProcess,
        allDocsWithUnpublished: typeof allDocumentsWithUnpublishedVersions,
        documentCount: number,
        allHaveLatestPublished: boolean,
        includeLatestPublished: boolean
    ): number => {
        if (mode === BulkDocumentActionMode.LatestUnpublished) return documentCount;
        if (allHaveLatestPublished && includeLatestPublished) {
            return allDocsWithUnpublished.reduce((total, doc) => total + doc.unpublishedVersionsCount, 0);
        }
        return filteredDocs.reduce((total, doc) => total + doc.unpublishedVersionsCount, 0);
    };

    const documentActionItems = useMemo<DocumentActionItem[]>(
        () =>
            documentsToProcess.map((doc) => ({
                documentId: doc.documentId,
                residentialUnitIds:
                    'linkedUnits' in doc ? doc.linkedUnits.map((unit) => unit.residentialUnitId) : [...residentialUnitIds],
            })),
        [documentsToProcess, residentialUnitIds]
    );

    const totalAffectedUnits = useMemo(
        () =>
            residentialUnitIds.length > 0
                ? residentialUnitIds.length
                : documentActionItems.reduce((total, doc) => total + doc.residentialUnitIds.length, 0),
        [documentActionItems, residentialUnitIds]
    );

    const totalVersionsToPublish = useMemo(
        () =>
            calculateTotalVersionsToPublish(
                approvalMode,
                documentsToProcess,
                allDocumentsWithUnpublishedVersions,
                documentActionItems.length,
                allSelectedDocumentsHaveLatestPublished,
                publishAllVersionsEvenIfLatestPublished
            ),
        [
            documentsToProcess,
            documentActionItems.length,
            approvalMode,
            allSelectedDocumentsHaveLatestPublished,
            publishAllVersionsEvenIfLatestPublished,
            allDocumentsWithUnpublishedVersions,
        ]
    );

    const bulkApprovalRequest = useMemo(
        () => ({
            docsToApprove: documentActionItems,
            approvalMode,
            sendEmail: false,
            emailMessage: '',
        }),
        [documentActionItems, approvalMode]
    );

    const handleSave = async () => {
        setSaving(true);

        try {
            await bulkApproveResidentialUnitDocument(bulkApprovalRequest);
            Toast.success(<Trans>Documents published successfully</Trans>);

            const uniqueUnitIds = uniq(documentActionItems.flatMap((doc) => doc.residentialUnitIds));
            triggerHandler(onApprove, uniqueUnitIds, {});
            triggerHandler(onClose);
        } catch (error) {
            Toast.error(<Trans>Failed to publish documents</Trans>);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => triggerHandler(onClose);

    const renderBanner = () => {
        if (allSelectedDocumentsHaveLatestPublished) {
            return (
                <Banner type="warning">
                    <Trans>
                        You have selected {countOfDocsWithLatestPublished} document
                        {countOfDocsWithLatestPublished !== 1 ? 's' : ''} where the latest version is already published.
                        Would you like to publish all unpublished versions?
                    </Trans>
                </Banner>
            );
        }

        if (isPublishingAllVersions) {
            return (
                <Banner type="warning">
                    <Trans>
                        <strong>Please note</strong> - when you publish all unpublished versions of these documents they will
                        be visible to the buyers of the selected units (in total: {totalVersionsToPublish} documents will be
                        published for {totalAffectedUnits} units).
                    </Trans>
                </Banner>
            );
        }

        return (
            <Banner type="info">
                <Trans>
                    <strong>Please note</strong> - when you publish these documents they will be visible to the buyers of the
                    selected units.
                </Trans>
            </Banner>
        );
    };

    const canPublishDocuments =
        documentActionItems.length > 0 &&
        !(allSelectedDocumentsHaveLatestPublished && !publishAllVersionsEvenIfLatestPublished);

    const shouldShowVersionOptions =
        hasMultipleVersions || hasDocsWithLatestPublished || allSelectedDocumentsHaveLatestPublished;
    const shouldShowModeSelection =
        (hasMultipleVersions || hasDocsWithLatestPublished) && !allSelectedDocumentsHaveLatestPublished;
    const shouldShowPublishAllCheckbox =
        (isPublishingAllVersions || allSelectedDocumentsHaveLatestPublished) && hasDocsWithLatestPublished;
    const shouldIndentCheckbox = shouldShowModeSelection;

    if (saving) return <LoadingIndicator />;

    return (
        <Modal size="xl" isOpen={show} toggle={handleCancelClick}>
            <ModalHeader>
                {allSelectedDocumentsHaveLatestPublished && !publishAllVersionsEvenIfLatestPublished ? (
                    <Trans>Publish documents</Trans>
                ) : (
                    <Trans>
                        Publish {totalVersionsToPublish} document{totalVersionsToPublish !== 1 ? 's' : ''}
                    </Trans>
                )}
            </ModalHeader>
            <ModalBody>
                {renderBanner()}
                {shouldShowVersionOptions && (
                    <div className="form-group">
                        <label>
                            <Trans>Version publishing options</Trans>
                        </label>
                        {shouldShowModeSelection && (
                            <>
                                <Checkbox
                                    checked={isPublishingLatestOnly}
                                    onChange={() => setApprovalMode(BulkDocumentActionMode.LatestUnpublished)}>
                                    <Trans>Publish only latest version of each document</Trans>
                                </Checkbox>
                                <Checkbox
                                    checked={isPublishingAllVersions}
                                    onChange={() => setApprovalMode(BulkDocumentActionMode.AllUnpublished)}>
                                    <Trans>Publish all unpublished versions</Trans>
                                </Checkbox>
                            </>
                        )}
                        {shouldShowPublishAllCheckbox && (
                            <Checkbox
                                checked={publishAllVersionsEvenIfLatestPublished}
                                onChange={(e) => setPublishAllVersionsEvenIfLatestPublished(e.target.checked)}
                                className={shouldIndentCheckbox ? theme.indentedCheckbox : theme.noIndentCheckbox}>
                                <Trans>Publish all unpublished versions even when latest version is already published</Trans>
                            </Checkbox>
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button disabled={!canPublishDocuments} color="success" onClick={handleSave} loading={saving}>
                    {!canPublishDocuments ? (
                        <Trans>Select an option</Trans>
                    ) : (
                        <Trans>
                            Publish {totalVersionsToPublish} document{totalVersionsToPublish !== 1 ? 's' : ''}
                        </Trans>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DocumentApproveMultipleDialog;
